/**
 * 등락(수익/손익/수익률/등락률) 표시 단일 유틸
 *
 * - 색: theme.palette.gain / loss / flat 토큰 (ThemeContext 가 주입).
 *   한국식(상승 빨강/하락 파랑) <-> 글로벌식(상승 초록/하락 빨강) 은 토큰 값만 바뀐다.
 * - 0(및 null/undefined/NaN)은 항상 flat.
 * - 색만으로 의미를 전달하지 않도록 부호(+/−)와 아이콘(getTrendIcon)을 함께 쓴다.
 *
 * 사용 예:
 *   <Typography sx={{ color: getTrendColor(profit) }}>{formatSigned(profit, { prefix: '₩', digits: 0 })}</Typography>
 *   const Icon = getTrendIcon(profit);  // TrendingUp | TrendingDown | Remove
 *
 * 손익과 무관한 에러/성공 상태(알림, 검증 메시지)에는 쓰지 말 것 (success/error 유지).
 */
import Remove from '@mui/icons-material/Remove';
import TrendingDown from '@mui/icons-material/TrendingDown';
import TrendingUp from '@mui/icons-material/TrendingUp';
import { alpha } from '@mui/material/styles';

export const TREND_GAIN = 'gain';
export const TREND_LOSS = 'loss';
export const TREND_FLAT = 'flat';

const MINUS = '−'; // U+2212 MINUS SIGN

export const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  const n = typeof value === 'number' ? value : Number(value);
  return n;
};

/**
 * value 의 방향. 0 / null / NaN 은 flat.
 *
 * precision(표시 소수 자릿수)을 주면 그 자릿수로 반올림한 값이 0 일 때도 flat 이다.
 * 화면에는 `0.00%` 로 보이는데 색/아이콘만 상승·하락인 불일치를 막기 위해,
 * 표시에 쓰는 자릿수(`formatSigned` 의 digits)와 같은 값을 넘길 것. 생략하면 원시 값의 부호로 판정한다.
 */
export const getTrendKind = (value, precision) => {
  const n = toNumber(value);
  if (!Number.isFinite(n) || n === 0) return TREND_FLAT;
  if (Number.isInteger(precision) && Number(Math.abs(n).toFixed(Math.min(Math.max(precision, 0), 20))) === 0) {
    return TREND_FLAT;
  }
  return n > 0 ? TREND_GAIN : TREND_LOSS;
};

/**
 * sx color/bgcolor 에 바로 쓰는 팔레트 키: 'gain.main' | 'loss.main' | 'flat.main'
 * 두 번째 인자에 숫자를 넘기면 precision 으로 해석한다: `getTrendColor(rate, 2)` (shade 는 'main').
 */
export const getTrendColor = (value, shade = 'main', precision) => {
  const [resolvedShade, resolvedPrecision] =
    typeof shade === 'number' ? ['main', shade] : [shade, precision];
  return `${getTrendKind(value, resolvedPrecision)}.${resolvedShade}`;
};

const FALLBACK_PALETTE_KEY = {
  [TREND_GAIN]: 'success',
  [TREND_LOSS]: 'error',
};

/**
 * theme 객체에서 실제 색 문자열을 꺼낸다 (Chart.js 데이터셋, alpha() 등 JS 에서 값이 필요한 곳).
 * gain/loss 토큰이 없는 테마(테스트용 기본 테마)는 success/error/text.secondary 로 폴백.
 */
export const getTrendColorValue = (theme, value, shade = 'main', precision) => {
  const kind = getTrendKind(value, precision);
  const palette = theme?.palette || {};
  if (palette[kind]?.[shade]) return palette[kind][shade];
  const fallbackKey = FALLBACK_PALETTE_KEY[kind];
  if (fallbackKey) return palette[fallbackKey]?.[shade] || palette[fallbackKey]?.main;
  return palette.text?.secondary;
};

/** 외곽선 Chip 용 sx (color/borderColor). Chip 은 color="default" 로 두고 sx 로 넘길 것 */
export const getTrendOutlinedSx = (value, precision) => {
  const key = getTrendColor(value, 'main', precision);
  return { color: key, borderColor: key };
};

/** 채움 Chip/Badge 용 sx (bgcolor + 대비 보장 글자색) */
export const getTrendFilledSx = (value, precision) => {
  const kind = getTrendKind(value, precision);
  return { bgcolor: `${kind}.main`, color: `${kind}.contrastText` };
};

/** 아바타/배지용 연한 배경(alpha 16%) + 토큰 글자색. 라이트/다크 모두 하드코딩 없이 대응 */
export const getTrendSoftSx = (value, precision) => ({
  backgroundColor: (theme) =>
    alpha(getTrendColorValue(theme, value, 'main', precision) || theme.palette.text.secondary, 0.16),
  color: getTrendColor(value, 'main', precision),
});

/** 아이콘 컴포넌트: 상승 TrendingUp / 하락 TrendingDown / 보합 Remove */
export const getTrendIcon = (value, precision) => {
  const kind = getTrendKind(value, precision);
  if (kind === TREND_GAIN) return TrendingUp;
  if (kind === TREND_LOSS) return TrendingDown;
  return Remove;
};

/** 접근성용 방향 라벨 */
export const getTrendLabel = (value, precision) => {
  const kind = getTrendKind(value, precision);
  if (kind === TREND_GAIN) return '상승';
  if (kind === TREND_LOSS) return '하락';
  return '보합';
};

/** 부호 문자만: '+' | '−' | '' (0 은 부호 없음) */
export const getTrendSign = (value, precision) => {
  const kind = getTrendKind(value, precision);
  if (kind === TREND_GAIN) return '+';
  if (kind === TREND_LOSS) return MINUS;
  return '';
};

/**
 * 부호를 붙여 포맷한다. 양수 '+', 음수 '−', 0 은 부호 없음.
 * 표시 자릿수로 반올림했을 때 0 이 되면 부호를 붙이지 않는다 ("−0.00%" 방지).
 *
 * @param {number|string|null} value
 * @param {object} [options]
 * @param {number} [options.digits=2]     소수 자릿수
 * @param {string} [options.prefix='']    부호 뒤 접두 (예: '₩', '$')
 * @param {string} [options.suffix='']    접미 (예: '%', '원')
 * @param {string} [options.fallback='-'] 값이 없을 때 표시
 * @param {function} [options.format]     절댓값 포맷터 (기본 ko-KR toLocaleString)
 */
export const formatSigned = (value, options = {}) => {
  const {
    digits = 2,
    prefix = '',
    suffix = '',
    fallback = '-',
    format,
  } = options;
  const n = toNumber(value);
  if (!Number.isFinite(n)) return fallback;

  const abs = Math.abs(n);
  const body = format
    ? format(abs)
    : abs.toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const rounded = Number(abs.toFixed(Math.min(Math.max(digits, 0), 20)));
  const sign = rounded === 0 ? '' : (n > 0 ? '+' : MINUS);
  return `${sign}${prefix}${body}${suffix}`;
};
